import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Grid,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  Event,
  Person,
  Store,
  Schedule,
  Visibility,
  Close,
} from '@mui/icons-material';
import { getBookings, getBookingDetails } from '../services/bookings';

const BookingDetailDialog = ({ open, onClose, booking }) => {
  if (!booking) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#374151',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Event sx={{ color: '#4f46e5' }} />
          Booking Details
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  }}
                >
                  <Person sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {booking.customerName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Booking ID: #{booking.id}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Shop Name
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {booking.shopName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Status
              </Typography>
              <Chip
                label={booking.status}
                size="small"
                color={
                  booking.status === 'completed' ? 'success' :
                  booking.status === 'pending' ? 'warning' :
                  booking.status === 'cancelled' ? 'error' : 'default'
                }
                sx={{ textTransform: 'capitalize' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {booking.date}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Time
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 16 }} />{booking.time}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await getBookings();
      // Handle various response formats
      let bookingsData = [];
      if (Array.isArray(response)) {
        bookingsData = response;
      } else if (response && Array.isArray(response.data)) {
        bookingsData = response.data;
      } else if (response && response.data && Array.isArray(response.data.bookings)) {
        bookingsData = response.data.bookings;
      } else if (response?.data?.data) {
        bookingsData = response.data.data;
      } else if (response && typeof response === 'object') {
        const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
        bookingsData = possibleArrays.length > 0 ? possibleArrays[0] : [];
      }
      
      // Map API response to component expected format
      const mappedBookings = bookingsData.map(booking => ({
        id: booking.id,
        customerName: `User ${booking.userId || 'Unknown'}`,
        shopName: booking.Shop?.name || `Shop ${booking.shopId || 'Unknown'}`,
        date: booking.bookingDate || '',
        time: booking.bookingTime || '',
        status: booking.statusId === 1 ? 'pending' : 
                booking.statusId === 2 ? 'completed' : 
                booking.statusId === 3 ? 'cancelled' : 'pending',
        createdAt: booking.createdAt,
      }));
      
      setBookings(mappedBookings);
    } catch (err) {
      setError('Failed to fetch bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredBookings = bookings.filter((booking) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      booking.customerName?.toLowerCase().includes(searchLower) ||
      booking.shopName?.toLowerCase().includes(searchLower) ||
      booking.status?.toLowerCase().includes(searchLower)
    );
  });

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Bookings Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          View and manage all bookings across shops
        </Typography>
      </Box>

      {/* Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#4f46e5',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchBookings}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#4f46e5', borderColor: '#4f46e5' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      {/* Bookings Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#4f46e5' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Shop</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Event sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                            No bookings found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {searchTerm
                              ? 'Try adjusting your search criteria'
                              : 'No bookings available yet'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((booking, index) => (
                        <TableRow
                          key={booking.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{booking.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                }}
                              >
                                <Person sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Typography variant="body2" fontWeight={500}>
                                {booking.customerName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Store sx={{ fontSize: 16, color: '#6b7280' }} />
                              <Typography variant="body2">{booking.shopName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Typography variant="body2">{booking.date}</Typography>
                              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                {booking.time}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.status}
                              size="small"
                              color={getStatusColor(booking.status)}
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                                textTransform: 'capitalize',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(booking)}
                                sx={{
                                  color: '#6b7280',
                                  '&:hover': { color: '#4f46e5' },
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredBookings.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Booking Detail Dialog */}
      <BookingDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        booking={selectedBooking}
      />
    </Box>
  );
};

export default Bookings;

